import Accessor from "@arcgis/core/core/Accessor";
import {
  property,
  subclass,
} from "@arcgis/core/core/accessorSupport/decorators";
import { when } from "@arcgis/core/core/reactiveUtils";
import MapView from "@arcgis/core/views/MapView";
import SceneView from "@arcgis/core/views/SceneView";
import EsriMap from "@arcgis/core/Map";

import MyAppsViewModel from "../../data/MyApps/MyAppsViewModel";
import ItemLoaderViewModel from "../../data/ItemLoader/ItemLoaderViewModel";
import AppTypeInfoViewModel from "../../data/AppTypeInfo/AppTypeInfoViewModel";
import PublishAppViewModel from "../../data/PublishApp/PublishAppViewModel";
import QueryStringViewModel from "../../data/QueryString/QueryStringViewModel";

import {
  CompatibilityChecker,
  EAppTemplateType,
} from "templates-common-library/baseClasses/CompatibilityChecker";
import AppTypeTile, {
  AppTypeTileProperties,
  EResourceType,
  resourceItem,
} from "../../widgets/AppTypeTile/AppTypeTile";

import appMessagesT9N from "../../../t9n/App/App.json";
import { findAppTypeBadge, isEnterprise } from "../../utils/appUtils";

import Telemetry from "@esri/telemetry";
import NavBarViewModel from "../NavBar/NavBarViewModel";
import AlertWrapper from "../../widgets/AlertWrapper/AlertWrapper";
import Collection from "@arcgis/core/core/Collection";
import { decideScreenshotUrl } from "../../utils/utils";

const FEATURED_TYPE_KEYWORD = "InstantAppGallery:Featured";

@subclass("Landing.ui.ContentArea.ContentAreaViewModel")
export default class ContentAreaViewModel extends Accessor {
  @property()
  view: __esri.MapView | __esri.SceneView;

  @property()
  portal: __esri.Portal;

  @property()
  telemetry: Telemetry;

  @property()
  itemLoaderVM: ItemLoaderViewModel = new ItemLoaderViewModel();

  @property()
  myAppsDataVM: MyAppsViewModel = new MyAppsViewModel();

  @property()
  appTypeInfoVM: AppTypeInfoViewModel = new AppTypeInfoViewModel();

  @property()
  publishAppVM: PublishAppViewModel = new PublishAppViewModel();

  @property()
  queryStringVM: QueryStringViewModel = new QueryStringViewModel();

  @property()
  notCompatible: Map<EAppTemplateType, string>;

  @property()
  appMessages: typeof appMessagesT9N;

  @property()
  activeAlerts: __esri.Collection<AlertWrapper> = new Collection();

  @property()
  navbarVM: NavBarViewModel;

  @property()
  browseAllTiles: AppTypeTile[];

  private _compatibilityChecker: CompatibilityChecker;

  constructor(params?: any) {
    super(params);

    // Populate DataVMs with portal
    when(
      () => this?.portal !== undefined,
      () => {
        this.itemLoaderVM.portal = this.portal;
        this.myAppsDataVM.portal = this.portal;
        this.appTypeInfoVM.portal = this.portal;
        this.queryStringVM.portal = this.portal;

        this.publishAppVM.portal = this.portal;
        this.publishAppVM.navbarVM = this.navbarVM;
        this.publishAppVM.telemetry = this.telemetry;
        this.publishAppVM.myAppsDataVM = this.myAppsDataVM;

        this.navbarVM.telemetry = this.telemetry;
        this.navbarVM.itemLoaderVM = this.itemLoaderVM;
        this.navbarVM.myAppsDataVM = this.myAppsDataVM;
        this.navbarVM.appTypeInfoVM = this.appTypeInfoVM;

        // Coordinate any other interactions that need to happen between the DataVMs
        when(
          () =>
            this?.queryStringVM?.defaultTab !== undefined ||
            this?.queryStringVM?.defaultAppId !== undefined,
          () => {
            this.navbarVM.setNavbarRoute(this.queryStringVM.defaultTab);
          },
          {
            initial: true,
            once: true,
          }
        );
      },
      {
        initial: true,
        once: true,
      }
    );

    when(
      () => this?.appMessages !== undefined,
      () => {
        this.publishAppVM.appMessages = this.appMessages;
        const { resourceMessages, requirementsMessages } = this.appMessages;
        // Not Compat Class Instantiate
        this._compatibilityChecker = new CompatibilityChecker({
          resourceMessages,
          requirementsMessages,
        });
      },
      {
        initial: true,
        once: true,
      }
    );

    this.handleCompatCheck();
  }

  handleCompatCheck(): void {
    when(
      () => this?.itemLoaderVM?.isItemLoaderDone === true,
      () => {
        switch (this.itemLoaderVM.loadedItemType) {
          case "webmap":
            this._handleWebmap();
            break;
          case "webscene":
            this._handleWebscene();
            break;
          case "group":
            this._handleGroup();
            break;
        }
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  /**
   * Takes in the PortalItem for the AppType and formats the data for ease of use
   * @param item - PortalItem representing the AppType (ex: Item for Attachment Viewer, Item for Media... etc)
   */
  formatAppTypeItems(item: __esri.PortalItem, index: number): any {
    const templateType: EAppTemplateType = item.url as EAppTemplateType;
    const resourceItem: resourceItem =
      this?.itemLoaderVM?.webmap?.portalItem ||
      this?.itemLoaderVM?.webscene?.portalItem ||
      this?.itemLoaderVM?.group;
    const sanitizedThumbnail = this._sanitizeThumbnailUrl(item.thumbnailUrl);
    return {
      uniqueTileId: `${index}`,
      resourceInfo: {
        resourceItem,
        resourceType: this._findResourceType(resourceItem),
      },
      contentAreaVM: this,
      publishAppVM: this.publishAppVM,
      appTypeInfo: {
        screenshotUrl: decideScreenshotUrl(item, templateType, this.portal),
        thumbnailUrl: sanitizedThumbnail,
        created: item.created,
        badge: findAppTypeBadge(templateType),
        appTypeTitle: this._titleFilter(item.title),
        appTypeUrlFragment: templateType,
        appTypeUrl: `${this.publishAppVM.orgUrl}${item.url}`.replace(
          "http://",
          "https://"
        ),
        compatibleMsgs: this.notCompatible?.get(templateType),
        appTypeId: item.id,
        portalItem: item,
      },
    };
  }

  filterFeaturedApps(item: __esri.PortalItem): boolean {
    const index = item.typeKeywords.findIndex((keyword: string) => {
      return keyword === FEATURED_TYPE_KEYWORD;
    });

    return index !== -1;
  }

  createView(originalData: unknown[]): unknown[] {
    if (originalData != null) {
      return [...originalData];
    } else {
      return [];
    }
  }

  /** Configure Alert Notification properly */
  showAlert(
    title: string,
    msg: string,
    type: "success" | "error",
    goToLinkId?: string
  ) {
    const alert = new AlertWrapper({
      title,
      msg,
      type,
      goToLinkId,
    });

    this.activeAlerts.add(alert);

    when(
      () => alert?.isDestroyed === true,
      () => {
        if (alert?.isDestroyed) {
          this.activeAlerts.remove(alert);
        }
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  /** Filter everything past "(" out of Title string */
  private _titleFilter(title: string): string {
    if (title.toLocaleLowerCase().indexOf("media map") !== -1) {
      return title;
    }
    const endPosition: number = title.indexOf("(");
    return title
      .substring(0, endPosition !== -1 ? endPosition : undefined)
      .trim();
  }

  private _findResourceType(resourceItem: resourceItem): EResourceType {
    const resourceUnknown: any = resourceItem;
    if (resourceUnknown == null) {
      return EResourceType.None;
    } else if (resourceUnknown.fetchMembers != null) {
      return EResourceType.Group;
    } else if (resourceUnknown.type === "Web Map") {
      return EResourceType.Webmap;
    } else if (resourceUnknown.type === "Web Scene") {
      return EResourceType.Webscene;
    } else {
      return EResourceType.Unknown;
    }
  }

  /** Strips the token out of the Url, if it exists */
  private _sanitizeThumbnailUrl(thumbnailUrl: string) {
    if (thumbnailUrl.indexOf("token") !== -1) {
      return thumbnailUrl
        .split("&")
        .filter((thumbnailPart: string) => {
          return !thumbnailPart.startsWith("token");
        })
        .join("&");
    } else {
      return thumbnailUrl;
    }
  }

  private _handleWebmap(): void {
    const webmap = this.itemLoaderVM.webmap;
    when(
      () => webmap?.loaded === true,
      async () => {
        this.notCompatible = await this._compatibilityChecker.checkAllTemplates(
          webmap
        );
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  private _handleWebscene(): void {
    const webscene = this.itemLoaderVM.webscene;
    when(
      () => webscene?.loaded === true,
      async () => {
        this.notCompatible = await this._compatibilityChecker.checkAllTemplates(
          webscene
        );
      },
      {
        initial: true,
        once: true,
      }
    );
  }

  private async _handleGroup(): Promise<void> {
    this.notCompatible = await this._compatibilityChecker.checkAllTemplates(
      "group"
    );
  }
}
